import h from 'hyperscript';

import checkIcon from '@disy/cadenza-icons/check.svg';
import { assertUnreachable } from '@disy/lib/custom-error';
import { formatPercent } from '@disy/lib/format';
import { icon } from '@disy/lib/icon';
import { clamp } from '@disy/lib/math';
import { uniqueId } from '@disy/lib/unique-id';
import type { Content } from '@disy/ui/content';
import { normalizeContent } from '@disy/ui/content';
import iconStyles from '@disy/ui/icon.css' with { type: 'css' };

import styles from './progress-bar.module.css' with { type: 'css' };
import i18n from './progress-bar.properties';

const COMPONENT_NAME = 'd-progress-bar';

/**
 * Represents a progress bar web component with a native progress-bar at the top, a label at the bottom left
 * and a value text label at the bottom right.
 *
 * Value text label format depends on max attribute. If max attribute is set, then the format is "<N>/<max>",
 * otherwise the value is shown in percents "<X>%". The text label can show any text by supplying a function
 * to "valueTextFormatter".
 *
 * _Note:_ Unfortunately, we cannot use the native <progress> element,
 * because it cannot be animated in Chrome, see
 * https://bugs.chromium.org/p/chromium/issues/detail?id=486195.
 *
 * @example
 *   <!-- no max attribute given, range is from 0 to 1, value text label will be formatted to "60%" -->
 *   <d-progress-bar value="0.6" label="Percent progress bar" />
 * @example
 *   <!-- max attribute is given, range is from 0 to max=5, value text label will be formatted to "3/5" -->
 *   <d-progress-bar max="5" value="3" label="Done count progress bar" />
 * @example
 *   // Customize the text label by overwriting the valueTextFormatter function.
 *   const progressBar = h('d-progress-bar');
 *   progressBar.valueTextFormatter = () => `${formatPercent(progressBar.value/progressBar.max}% (${progressBar.value} of ${progressBar.max})`;
 */

type ProgressBarState = 'ACTIVE' | 'FAILED' | 'STALLED' | 'ABORTED' | 'SUCCESS';

interface ProgressBarOptions {
  label?: string;
}

function i18nProgressBarState(value: ProgressBarState) {
  return i18n(`label.state.<${value}>`);
}

export class ProgressBar extends HTMLElement {
  override shadowRoot!: ShadowRoot;

  _progressBar: HTMLElement;
  _progressValue: HTMLElement;
  _label: HTMLElement;
  _valueText: HTMLElement;

  _isPercent!: boolean; // The max property (where this one is set) is assigned in the constructor.
  _state: ProgressBarState = 'ACTIVE';
  _labelContent?: Content;
  _valueTextFormatter?: () => string;

  constructor(options: ProgressBarOptions = {}) {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot.adoptedStyleSheets = [styles, iconStyles];

    const labelId = uniqueId();
    this.shadowRoot.append(
      (this._progressBar = h(
        '.d-progress-bar--progress-bar',
        {
          attrs: {
            role: 'progressbar',
            'aria-labelledby': labelId,
          },
        },
        [(this._progressValue = h('.d-progress-bar--progress-value'))],
      )),
      h('.d-progress-bar--labels-container', [
        (this._label = h('span.d-progress-bar--label', { id: labelId })),
        (this._valueText = h('span.d-progress-bar--valuetext', { attrs: { 'aria-hidden': 'true' } })),
      ]),
    );

    this.max = undefined;
    this.value = undefined;
    this.label = options.label ?? '';
  }

  connectedCallback() {
    this.classList.add(COMPONENT_NAME);

    if (this.hasAttribute('max')) {
      this.max = Number(this.getAttribute('max'));
    }
    if (!this.label) {
      this.label = this.getAttribute('label') ?? '';
    }
    if (this.hasAttribute('value')) {
      this.value = Number(this.getAttribute('value'));
    }
  }

  get max(): number {
    return Number(this._progressBar.getAttribute('aria-valuemax'));
  }

  set max(max: number | undefined) {
    this._isPercent = max == null;
    this._progressBar.setAttribute('aria-valuemax', String(Math.max(0, max ?? 1)));
  }

  set label(value) {
    this._labelContent = value;
    this._updateLabel();
  }

  get label() {
    return this._labelContent;
  }

  set state(value) {
    this.classList.remove(`is-${this._state.toLowerCase()}`);
    this._state = value;
    this.classList.add(`is-${this._state.toLowerCase()}`);
    this._updateLabel();
  }

  get state() {
    return this._state;
  }

  set value(value: number | undefined) {
    this._progressBar.setAttribute('aria-valuenow', String(clamp(value ?? 0, 0, this.max)));
    this._update();
  }

  get value(): number {
    return Number(this._progressBar.getAttribute('aria-valuenow'));
  }

  set valueTextFormatter(value) {
    this._valueTextFormatter = value;
    this._update();
  }

  get valueTextFormatter() {
    return this._valueTextFormatter;
  }

  _update() {
    const progress = this.value / this.max;
    this._progressValue.style.setProperty('--progress-value', String(progress));

    if (progress === 1) {
      this.state = 'SUCCESS';
    }

    const formattedValue = this._formatValue(this.value);
    this._progressBar.setAttribute('aria-valuetext', formattedValue);
    this._valueText.textContent = formattedValue;
  }

  _formatValue(value: number) {
    if (this.valueTextFormatter) {
      return this.valueTextFormatter();
    }
    return this._isPercent ? formatPercent(value) : doneCountFormatter(value, this.max);
  }

  _updateLabel() {
    this._label.innerHTML = '';
    this._label.append(...this._getLabelContent());
  }

  _getLabelContent() {
    const content = normalizeContent(this._labelContent);
    switch (this._state) {
      case 'ACTIVE':
      case 'STALLED':
        return content;
      case 'ABORTED':
        return [i18nProgressBarState('ABORTED')];
      case 'FAILED': {
        if (content.length) {
          content.push(' ');
        }
        content.push(i18nProgressBarState('FAILED'));
        return content;
      }
      case 'SUCCESS':
        content.push(icon(checkIcon));
        return content;
    }
    return assertUnreachable(this._state);
  }
}

function doneCountFormatter(value: number, max: number) {
  return `${value}/${max}`;
}

customElements.define(COMPONENT_NAME, ProgressBar);
declare global {
  interface HTMLElementTagNameMap {
    [COMPONENT_NAME]: ProgressBar;
  }
}
